import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { createChart, IChartApi, ISeriesApi, CandlestickData, HistogramData } from 'lightweight-charts'
import { useTradingStore } from '../hooks/useTradingStore'

const TIMEFRAMES = {
  '1m': 1,
  '5m': 5,
  '30m': 30,
  '1h': 60,
  '4h': 240,
} as const
type Timeframe = keyof typeof TIMEFRAMES

interface AggregatedBar {
  timestamp: number  // nanoseconds since epoch (matches BarData)
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export default function PriceChart() {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  const emaFastRef = useRef<ISeriesApi<'Line'> | null>(null)
  const emaSlowRef = useRef<ISeriesApi<'Line'> | null>(null)

  const { bars, trades, indicators } = useTradingStore()
  const [showMACD, setShowMACD] = useState(true)
  const [showEMA, setShowEMA] = useState(true)
  const [timeframe, setTimeframe] = useState<Timeframe>('1m')

  // Aggregate the 1-minute bars from the store into the selected timeframe.
  // Bucket by floor(seconds_since_epoch / bucket_seconds): O = first bar's open,
  // H/L = max/min within bucket, C = last bar's close, V = sum.
  // Convert ns → s before bucketing to keep arithmetic within Number's safe range
  // (full ns timestamps overflow MAX_SAFE_INTEGER for present-day epoch values).
  const aggregatedBars: AggregatedBar[] = useMemo(() => {
    const minutes = TIMEFRAMES[timeframe]
    if (minutes === 1) return bars
    const bucketSec = minutes * 60
    const buckets = new Map<number, AggregatedBar>()
    for (const bar of bars) {
      const sec = Math.floor(bar.timestamp / 1_000_000_000)
      const bucketKey = Math.floor(sec / bucketSec) * bucketSec
      const existing = buckets.get(bucketKey)
      if (!existing) {
        buckets.set(bucketKey, {
          timestamp: bucketKey * 1_000_000_000,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume,
        })
      } else {
        existing.high = Math.max(existing.high, bar.high)
        existing.low = Math.min(existing.low, bar.low)
        existing.close = bar.close
        existing.volume += bar.volume
      }
    }
    return Array.from(buckets.values()).sort((a, b) => a.timestamp - b.timestamp)
  }, [bars, timeframe])

  useEffect(() => {
    if (!chartContainerRef.current) return

    // Create main price chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 300,
      layout: {
        background: { color: '#111111' },
        textColor: '#a0a0a0',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1a1a1a' },
        horzLines: { color: '#1a1a1a' },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#4488ff',
          width: 1,
          style: 2,
          labelBackgroundColor: '#4488ff',
        },
        horzLine: {
          color: '#4488ff',
          width: 1,
          style: 2,
          labelBackgroundColor: '#4488ff',
        },
      },
      rightPriceScale: {
        borderColor: '#2a2a2a',
      },
      timeScale: {
        borderColor: '#2a2a2a',
        timeVisible: true,
        secondsVisible: false,
      },
    })

    chartRef.current = chart

    // Candlestick series
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#00ff88',
      downColor: '#ff4444',
      borderDownColor: '#ff4444',
      borderUpColor: '#00ff88',
      wickDownColor: '#ff4444',
      wickUpColor: '#00ff88',
    })
    candleSeriesRef.current = candleSeries

    // Volume series
    const volumeSeries = chart.addHistogramSeries({
      color: '#4488ff',
      priceFormat: { type: 'volume' },
      priceScaleId: '',
    })
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    })
    volumeSeriesRef.current = volumeSeries

    // EMA lines
    if (showEMA) {
      const emaFast = chart.addLineSeries({
        color: '#ffcc00',
        lineWidth: 1,
        title: 'EMA Fast',
      })
      emaFastRef.current = emaFast

      const emaSlow = chart.addLineSeries({
        color: '#ff6600',
        lineWidth: 1,
        title: 'EMA Slow',
      })
      emaSlowRef.current = emaSlow
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth })
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      // Null refs before dispose so dependent effects see them as gone
      candleSeriesRef.current = null
      volumeSeriesRef.current = null
      emaFastRef.current = null
      emaSlowRef.current = null
      chartRef.current = null
      chart.remove()
    }
  }, [showEMA])

  // Update chart data
  useEffect(() => {
    if (!candleSeriesRef.current || !volumeSeriesRef.current || aggregatedBars.length === 0) return

    // Format data for candlestick chart
    const candleData: CandlestickData[] = aggregatedBars.map((bar) => ({
      time: Math.floor(bar.timestamp / 1_000_000_000) as any,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    }))

    const volumeData: HistogramData[] = aggregatedBars.map((bar) => ({
      time: Math.floor(bar.timestamp / 1_000_000_000) as any,
      value: bar.volume,
      color: bar.close >= bar.open ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)',
    }))

    candleSeriesRef.current.setData(candleData)
    volumeSeriesRef.current.setData(volumeData)

    // Add trade markers — snap each trade's time to its enclosing aggregated
    // bucket so lightweight-charts can align the marker to a real bar.
    if (trades.length > 0) {
      const bucketSec = TIMEFRAMES[timeframe] * 60
      const markers = trades.map((trade) => {
        const sec = Math.floor(trade.timestamp / 1_000_000_000)
        const bucketKey = Math.floor(sec / bucketSec) * bucketSec
        const isBuy = trade.side === 'LONG' || trade.side === 'BUY'
        return {
          time: bucketKey as any,
          position: isBuy ? 'belowBar' : 'aboveBar',
          color: isBuy ? '#00ff88' : '#ff4444',
          shape: isBuy ? 'arrowUp' : 'arrowDown',
          text: `${trade.side} @ ${trade.price.toFixed(2)}`,
        }
      })
      candleSeriesRef.current.setMarkers(markers as any)
    }

  }, [aggregatedBars, trades, showEMA, timeframe])

  // Update EMA lines
  useEffect(() => {
    if (!emaFastRef.current || !emaSlowRef.current || !indicators.ema_fast || !indicators.ema_slow) return

    // Plot the latest EMA value at the latest *aggregated* bar's bucket time so
    // the line aligns with the candle the user is currently looking at.
    if (aggregatedBars.length > 0) {
      const lastBar = aggregatedBars[aggregatedBars.length - 1]
      const time = Math.floor(lastBar.timestamp / 1_000_000_000) as any

      try {
        emaFastRef.current.update({ time, value: indicators.ema_fast })
        emaSlowRef.current.update({ time, value: indicators.ema_slow })
      } catch {
        // series was disposed between the guard check and update — ignore
      }
    }
  }, [indicators.ema_fast, indicators.ema_slow, aggregatedBars])

  return (
    <div className="w-full h-full">
      {/* Chart Controls */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-1">
          {(Object.keys(TIMEFRAMES) as Timeframe[]).map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={clsx(
                'btn btn-outline text-xs py-1 px-2',
                timeframe === tf && 'bg-[#1a1a1a] text-white'
              )}
            >
              {tf}
            </button>
          ))}
        </div>
        <div className="h-4 w-px bg-[#333] mx-1" />
        <label className="flex items-center gap-1 text-xs text-[#a0a0a0] cursor-pointer">
          <input
            type="checkbox"
            checked={showEMA}
            onChange={(e) => setShowEMA(e.target.checked)}
            className="w-3 h-3"
          />
          EMA
        </label>
        <label className="flex items-center gap-1 text-xs text-[#a0a0a0] cursor-pointer">
          <input
            type="checkbox"
            checked={showMACD}
            onChange={(e) => setShowMACD(e.target.checked)}
            className="w-3 h-3"
          />
          MACD
        </label>
      </div>

      {/* Main Chart */}
      <div ref={chartContainerRef} className="w-full" style={{ height: showMACD ? '250px' : '350px' }} />

      {/* MACD Chart */}
      {showMACD && <MACDChart />}
    </div>
  )
}

function MACDChart() {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const { bars, indicators } = useTradingStore()

  useEffect(() => {
    if (!chartContainerRef.current) return

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 100,
      layout: {
        background: { color: '#111111' },
        textColor: '#a0a0a0',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1a1a1a' },
        horzLines: { color: '#1a1a1a' },
      },
      rightPriceScale: {
        borderColor: '#2a2a2a',
      },
      timeScale: {
        borderColor: '#2a2a2a',
        visible: false,
      },
    })

    chartRef.current = chart

    // MACD Line
    const macdLine = chart.addLineSeries({
      color: '#4488ff',
      lineWidth: 1,
      title: 'MACD',
    })

    // Signal Line
    const signalLine = chart.addLineSeries({
      color: '#ff6600',
      lineWidth: 1,
      title: 'Signal',
    })

    // Histogram
    const histogram = chart.addHistogramSeries({
      color: '#00ff88',
      priceFormat: { type: 'volume' },
    })

    // If we have indicator data, update chart
    if (indicators.macd && bars.length > 0) {
      const lastBar = bars[bars.length - 1]
      const time = Math.floor(lastBar.timestamp / 1_000_000_000) as any

      macdLine.update({ time, value: indicators.macd.value })
      signalLine.update({ time, value: indicators.macd.signal })

      const histogramColor = indicators.macd.histogram >= 0 ? '#00ff88' : '#ff4444'
      histogram.update({ time, value: indicators.macd.histogram, color: histogramColor })
    }

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth })
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chart is created once; data updates are applied by the effects below
  }, [])

  return (
    <div className="mt-10">
      <div className="text-xs text-[#666] mb-1">MACD (12, 26, 9)</div>
      <div ref={chartContainerRef} className="w-full h-[100px]" />
    </div>
  )
}
